<template>
    <div>
        <sonicu-header>Alarm Status</sonicu-header>
        <div v-if="hasData" :id="chartId" class="h-64"></div>
        <div v-else class="h-64 flex items-center justify-center text-sm text-gray-400">
            No alarms in this period.
        </div>
    </div>
</template>

<script>
import {iconNamesMixin} from "@/mixins/IconNamesMixin";
import {amchartsMixin} from "@/mixins/amchartsMixin";

import * as am5 from "@amcharts/amcharts5";
import * as am5percent from "@amcharts/amcharts5/percent";

export default {
    name: "RegionAlarmStatusPieChart",
    mixins: [iconNamesMixin, amchartsMixin],
    props: {
        // Unique DOM id for the amCharts root (amCharts requires a distinct element id per chart).
        chartId: {
            type: String,
            required: true,
        },
        // [{ status, count, color }]
        data: {
            type: Array,
            default: () => [],
        },
    },
    created() {
        // Non-reactive: this holds a live am5 Root. Vue's reactive() proxies
        // deeply, and am5 detaches a child's canvas display by === identity, so
        // an am5 object that exists as both a raw object and a proxy never gets
        // removed from its parent's display list and keeps painting as a ghost.
        // See the note in Am5Series' constructor.
        this.root = null;
    },
    computed: {
        // Nothing to plot when every status count is zero (no alarms in the window). Guarding here
        // both hides the empty chart and skips building the amCharts root entirely.
        hasData() {
            return this.data.some((row) => (row.count ?? 0) > 0);
        },
    },
    mounted() {
        if (!this.hasData) {
            return;
        }
        this.initializeAM5Charts();
        this.makeChart();
    },
    beforeUnmount() {
        if (this.root) {
            this.root.dispose();
        }
    },
    methods: {
        makeChart() {
            let root = am5.Root.new(this.chartId);

            let chart = root.container.children.push(
                am5percent.PieChart.new(root, {})
            );

            let series = chart.series.push(
                am5percent.PieSeries.new(root, {
                    radius: am5.percent(75),
                    valueField: "count",
                    categoryField: "status",
                    // Palette kept in sync with DashboardPointStatusSummary (Threshold = red,
                    // Offline = grey, Snoozed = caution yellow). This matches the fixed
                    // Threshold/Offline/Snoozed order the pie data is built in.
                    colors: am5.ColorSet.new(root, {
                        colors: [
                            am5.color("#c53030"),
                            am5.color("#a0aec0"),
                            am5.color("#f6e05e"),
                        ],
                    }),
                })
            );

            series.slices.template.setAll({
                toggleKey: "none",
                tooltipText: "{category}: {value}",
                propertyFields: {
                    fill: "color",
                },
                strokeOpacity: 1,
            });

            series.labels.template.setAll({
                text: "{category}: {value}",
                maxWidth: 90,
                wrap: true,
            });

            series.data.setAll(
                this.data.map((row) => ({
                    status: row.status,
                    count: row.count,
                    color: am5.color(row.color),
                }))
            );

            this.root = root;
        },
    },
};
</script>

<style scoped>
</style>
